import type { PublicAnnouncement } from 'types';
import { AlertTriangle, Info, Megaphone } from 'lucide-react';
import { ANNOUNCEMENT_SEVERITIES } from 'definitions';
import { cn } from 'helpers/cn';

type Severity = PublicAnnouncement['severity'];

type SeverityPickerProps = {
  value: Severity;
  onChange: (severity: Severity) => void;
};

type SeverityOption = {
  label: string;
  description: string;
  icon: typeof Info;
  /** The swatch stands in for the bar's own weight, so it borrows the status tone. */
  swatch: string;
  iconClass: string;
};

const SEVERITY_OPTIONS: Record<Severity, SeverityOption> = {
  neutral: {
    label: 'Neutral',
    description: 'Product news, low weight',
    icon: Megaphone,
    swatch: 'bg-field border-line',
    iconClass: 'text-dim',
  },
  info: {
    label: 'Info',
    description: 'Worth reading, dismissible',
    icon: Info,
    swatch: 'bg-sel-bg border-sel-line',
    iconClass: 'text-sel',
  },
  urgent: {
    label: 'Urgent',
    description: 'Cannot be dismissed',
    icon: AlertTriangle,
    swatch: 'bg-danger-bg border-danger-line',
    iconClass: 'text-danger',
  },
};

export const SeverityPicker = ({ value, onChange }: SeverityPickerProps) => {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="micro-label block">Severity</span>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {ANNOUNCEMENT_SEVERITIES.map((severity) => {
          const option = SEVERITY_OPTIONS[severity];
          const Icon = option.icon;
          const isSelected = value === severity;

          return (
            <button
              key={severity}
              type="button"
              onClick={() => onChange(severity)}
              aria-pressed={isSelected}
              className={cn(
                'rounded-card flex flex-col gap-2 border p-3 text-left transition-colors duration-150',
                isSelected ? 'border-sel-line bg-sel-bg' : 'border-line hover:bg-hover',
              )}
            >
              <span className={cn('rounded-lg flex h-8 w-full items-center border px-2', option.swatch)}>
                <Icon className={cn('h-4 w-4', option.iconClass)} />
              </span>
              <span className="text-foreground text-meta font-medium">{option.label}</span>
              <span className="supporting">{option.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
